import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Mail, ShieldCheck, CreditCard, LogIn, KeyRound, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailCheckModal() {
  const navigate = useNavigate();
  const { emailCheckOpen, closeEmailCheck, proceedToPayment, checkingOut, checkoutUrl } = useCart();
  const { user, isAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null); // null | check response

  // Prefill the logged-in user's email when the modal opens.
  useEffect(() => {
    if (emailCheckOpen) {
      if (user?.email) setEmail(user.email);
      setResult(null);
    }
  }, [emailCheckOpen, user]);

  if (!emailCheckOpen) return null;

  const isLoggedIn = isAuthenticated;

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your billing email");
      return;
    }
    setChecking(true);
    try {
      const res = await axios.post(`${API}/payments/check-subscription-email`, { email });
      const data = res.data || {};
      setResult(data);
      if (!data.has_active_subscription) {
        // Safe to proceed straight to Stripe with this billing email.
        await proceedToPayment(email);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not verify your account. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const handleManageBilling = async () => {
    if (!isLoggedIn) {
      toast.info("Please log in to manage your billing.");
      navigate("/subscribers/login");
      return;
    }
    try {
      const res = await axios.post(`${API}/payments/customer-portal`, { origin_url: window.location.origin });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open billing portal.");
    }
  };

  const blocked = result?.has_active_subscription;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4"
      data-testid="email-check-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md relative">
        <button
          onClick={closeEmailCheck}
          className="absolute right-3 top-3 text-slate-400 hover:text-slate-700"
          data-testid="email-check-close-btn"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {!blocked ? (
          <form onSubmit={handleContinue} className="p-7">
            <div className="w-14 h-14 bg-royal-blue/10 rounded-full flex items-center justify-center mb-4">
              <ShieldCheck className="w-7 h-7 text-royal-blue" />
            </div>
            <h2 className="font-heading text-2xl text-royal-blue mb-1">Check Your Account</h2>
            <p className="text-sm text-slate-600 mb-5">
              Enter the email you want to use for billing and portal access.
            </p>
            <Label className="form-label">Email</Label>
            <div className="relative mb-5">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input pl-10"
                placeholder="you@example.com"
                data-testid="email-check-input"
                style={{ fontSize: "16px" }}
                autoComplete="email"
              />
            </div>
            <Button
              type="submit"
              className="w-full btn-gold"
              disabled={checking || checkingOut}
              data-testid="email-check-continue-btn"
            >
              {checking || checkingOut ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…</>
              ) : (
                "Continue"
              )}
            </Button>
            {checkoutUrl && (
              <a
                href={checkoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center text-sm font-semibold text-royal-blue underline mt-3"
                data-testid="email-check-stripe-fallback"
              >
                Open secure Stripe checkout
              </a>
            )}
            <p className="text-[11px] text-center text-slate-400 mt-3">
              We check this first so you never create a duplicate subscription.
            </p>
          </form>
        ) : (
          <div className="p-7" data-testid="email-check-already-subscribed">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CreditCard className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="font-heading text-2xl text-royal-blue mb-1">You're Already Subscribed</h2>
            <p className="text-sm text-slate-600 mb-5">
              This email already has an active{result?.active_subscription_tier ? ` ${result.active_subscription_tier}` : ""}{" "}
              subscription. Manage your plan in Stripe Billing instead of creating a new one.
            </p>
            <div className="space-y-2">
              <Button className="w-full btn-gold" onClick={handleManageBilling} data-testid="already-sub-manage-billing-btn">
                <CreditCard className="w-4 h-4 mr-2" /> Manage Billing
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate("/subscribers/login")} data-testid="already-sub-login-btn">
                <LogIn className="w-4 h-4 mr-2" /> Log In to Portal
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate("/forgot-password")} data-testid="already-sub-forgot-btn">
                <KeyRound className="w-4 h-4 mr-2" /> Forgot Password
              </Button>
              <Button variant="ghost" className="w-full text-slate-500" onClick={closeEmailCheck} data-testid="already-sub-return-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Return to Cart
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
